import { forceCollide, forceLink, forceManyBody, forceSimulation, forceX, forceY } from "d3-force"

import type { Segment } from "../types"
import type { ConnectionRole } from "./segment-connections"
import type { Edge, Node } from "@xyflow/react"
import type { Simulation, SimulationLinkDatum, SimulationNodeDatum } from "d3-force"

import { buildConnectionsGraph, COMPANY_FILL, companyDisplayRole } from "./segment-connections"
import { marketColors } from "../data/mock-segment-analysis-data"

/**
 * Market bubbles share their segments' colour family (same market-colour fill)
 * but must read as a different KIND of node on the map, not just a bigger dot
 * (#3306 #14). A double ring — a gap in the canvas colour, then a second ring
 * in the same fill colour — stays in-family while giving the frame real
 * weight; plain segment/company bubbles keep the ordinary `shadow-sm`.
 *
 * The gap ring uses the `--card` token (the map canvas is `bg-card`), never a
 * hardcoded white — a literal white would show as a bright halo on the
 * off-black dark-mode canvas instead of a gap (review finding #3306 #7-4).
 */
export function marketBubbleFrame(color: string): { boxShadow: string } {
  return { boxShadow: `0 0 0 2px hsl(var(--card)), 0 0 0 4px ${color}` }
}

/**
 * A stale legend market-filter selection must not silently zero out the map:
 * the global Market/Owner chips can narrow `segments` out from under the
 * legend's own local filter while the Map stays mounted (#3306 #7 review
 * finding 1). Every read of the filter goes through this pruned view instead
 * of the raw selection, so a market that's no longer present drops out
 * instead of leaving an unsatisfiable filter behind.
 */
export function pruneMarketFilter(filter: Set<string>, markets: string[]): Set<string> {
  if (filter.size === 0) return filter
  const marketSet = new Set(markets)
  const pruned = new Set(Array.from(filter).filter((market) => marketSet.has(market)))
  return pruned.size === filter.size ? filter : pruned
}

// Circle radii — markets anchor clusters, segments scale with sales volume,
// companies are the small satellites (matching the network-graph reference).
const MARKET_RADIUS = 44
const SEGMENT_RADIUS_MIN = 26
const SEGMENT_RADIUS_MAX = 42
const COMPANY_RADIUS = 21

export type LayoutResult = {
  nodes: Node[]
  edges: Edge[]
  /** company node id -> a representative segment id to drill into */
  companySegment: Map<string, string>
}

export type SimNode = SimulationNodeDatum & { id: string; radius: number }
export type LiveSimulation = Simulation<SimNode, SimulationLinkDatum<SimNode>>

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Classic force-directed setup: springs along the edges, charge repulsion
 * between all nodes, collision padding so labels never overlap, and a gentle
 * centring pull. Shared between the initial synchronous layout and the live
 * drag simulation so reheating never rearranges a settled graph.
 */
export function createSimulation(
  simNodes: SimNode[],
  simLinks: Array<SimulationLinkDatum<SimNode>>
): LiveSimulation {
  return forceSimulation(simNodes)
    .force(
      "link",
      forceLink<SimNode, SimulationLinkDatum<SimNode>>(simLinks)
        .id((d) => d.id)
        .distance((link) => {
          const s = link.source as SimNode
          const t = link.target as SimNode
          return s.radius + t.radius + 46
        })
        .strength(0.5)
    )
    .force("charge", forceManyBody().strength(-220))
    .force(
      "collide",
      forceCollide<SimNode>()
        .radius((d) => d.radius + 6)
        .strength(0.9)
    )
    .force("x", forceX(0).strength(0.045))
    .force("y", forceY(0).strength(0.045))
    .stop()
}

function companyFill(roles: ConnectionRole[]): string {
  return COMPANY_FILL[companyDisplayRole(roles)]
}

export function computeLayout(segments: Segment[]): LayoutResult {
  const { nodes: modelNodes, edges: modelEdges } = buildConnectionsGraph(segments, marketColors)

  const maxSales = Math.max(1, ...segments.map((s) => s.sales))
  const radiusOf = (node: (typeof modelNodes)[number]): number => {
    if (node.kind === "market") return MARKET_RADIUS
    if (node.kind === "segment") {
      const t = Math.sqrt(node.sales / maxSales) // area ∝ sales
      return SEGMENT_RADIUS_MIN + t * (SEGMENT_RADIUS_MAX - SEGMENT_RADIUS_MIN)
    }
    return COMPANY_RADIUS
  }

  // Deterministic initial spread (golden-angle spiral) so the simulation starts
  // untangled and converges to the same layout for the same portfolio — no
  // Math.random, so snapshots and revisits look identical.
  const simNodes: SimNode[] = modelNodes.map((node, index) => {
    const angle = index * 2.39996
    const r = 40 + 26 * Math.sqrt(index)
    return {
      id: node.id,
      radius: radiusOf(node),
      x: Math.cos(angle) * r,
      y: Math.sin(angle) * r,
    }
  })
  const simLinks: Array<SimulationLinkDatum<SimNode>> = modelEdges.map((edge) => ({
    source: edge.source,
    target: edge.target,
  }))

  // Run the shared force setup synchronously to a settled state for the first
  // paint — the live drag simulation later reheats the same configuration.
  const simulation = createSimulation(simNodes, simLinks)

  for (let i = 0; i < 300; i += 1) simulation.tick()

  const positioned = new Map(simNodes.map((n) => [n.id, n]))

  const nodes: Node[] = modelNodes.map((node) => {
    const sim = positioned.get(node.id)!
    const radius = sim.radius
    const size = radius * 2
    const position = { x: (sim.x ?? 0) - radius, y: (sim.y ?? 0) - radius }
    const base = { selected: false, dimmed: false, size }
    if (node.kind === "market") {
      return {
        id: node.id,
        type: "bubble" as const,
        position,
        width: size,
        height: size,
        data: { ...base, label: node.label, color: node.color, kind: "market" as const },
      }
    }
    if (node.kind === "segment") {
      const marketColor = marketColors[node.marketName] ?? "#64748b"
      return {
        id: node.id,
        type: "bubble" as const,
        position,
        width: size,
        height: size,
        data: {
          ...base,
          label: node.label,
          color: marketColor,
          kind: "segment" as const,
          sales: node.sales,
        },
      }
    }
    return {
      id: node.id,
      type: "bubble" as const,
      position,
      width: size,
      height: size,
      data: {
        ...base,
        label: node.label,
        color: companyFill(node.roles),
        kind: "company" as const,
        roles: node.roles,
      },
    }
  })

  const edges: Edge[] = modelEdges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: "straight",
  }))

  const companySegment = new Map<string, string>()
  for (const edge of modelEdges) {
    if (edge.target.startsWith("company:") && !companySegment.has(edge.target)) {
      const seg = modelNodes.find((n) => n.id === edge.source && n.kind === "segment")
      if (seg && seg.kind === "segment") companySegment.set(edge.target, seg.segmentId)
    }
  }

  return { nodes, edges, companySegment }
}
